import { useEffect, useState } from "react";
import HubHeader from "../../components/ScheduleHub/HubHeader";
import DayActivityCard from "../../components/ScheduleHub/DayActivityCard";
import ProgressCard from "../../components/ScheduleHub/ProgressCard";
import StudyTimeProgress from "../../components/ScheduleHub/StudyTimeProgress";
import DisciplineReport from "../../components/ScheduleHub/DisciplineReport";
import WeekActivitySchedule from "../../components/ScheduleHub/WeekActivitySchedule";
import AdjustScheduleModal from "../../components/WeekSchedule/Modal/AdjustScheduleModal";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { useGetHubResume } from "../../hooks/ScheduleHub/useGetHubResume";
import BrainLoadingWrapper from "../../../../core/components/molecules/BrainLoading";
import { add, format } from "date-fns";
import { Typography } from "../../../../core/components/molecules/Typography";
import { usePlanConfigStore } from "../../stores/planConfigStore";
import { useNavigate } from 'react-router-dom';
import { getScheduleStartInfo, ScheduleStartInfo } from "../../utils/scheduleStart";

const ScheduleHub = () => {
  const navigate = useNavigate();
  const {
    data: resumo,
    isPending: isPendingResumo,
    isError: isErrorResumo,
  } = useGetHubResume();

  const { steps, setCurrentStep } = usePlanConfigStore((state) => ({
    steps: state.steps,
    setCurrentStep: state.setCurrentStep,
  }));

  const [scheduleChangeKey, setScheduleChangeKey] = useState(0);
  const [showAdjustScheduleModal, setShowAdjustScheduleModal] = useState(false);
  const [adjustScheduleAction, setAdjustScheduleAction] = useState<'edit' | 'delete'>('edit');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [startInfo, setStartInfo] = useState<ScheduleStartInfo | null>(null);
  
  useEffect(() => {
    if (resumo) {
      setStartInfo(getScheduleStartInfo(resumo.weeksArray, resumo.dataFirstContent || null));
    }
  }, [resumo]);

  if (isErrorResumo) {
    return (
      <div className='flex h-[20vh] flex-col items-center justify-center'>
        <Typography className='text-mono-color-dark'>
          Ocorreu um erro ao carregar o resumo do hub.
        </Typography>
      </div>
    );
  }

  if (isPendingResumo || !resumo) {
    return <BrainLoadingWrapper />;
  }
  

  const configStartPath = resumo?.id
    ? `/plano-adaptativo/${resumo.id}/configuracao/inicio`
    : '/plano-adaptativo/configuracao/inicio';

  return (
    <div className='flex w-full min-w-0 flex-col gap-5' data-tour="schedule-hub">
      <HubHeader 
        title={resumo.nome} 
        version={resumo.versao.toString()}
        createdAt={format(
          add(new Date(resumo.dataCriacao), { hours: 3 }),
          'dd/MM/yyyy'
        )}
        // onAdjustSchedule={() => setShowAdjustScheduleModal(true)}
        onAdjustSchedule={() => setShowDeleteModal(true)}
      />
      <div className='grid grid-cols-1 gap-md xl:grid-cols-2 xl:gap-xl'>
        <div className='col-span-1 flex flex-col gap-3'>
          <DayActivityCard
            cronogramaId={resumo.id}
            weeks={resumo.weeksArray}
            refreshTrigger={scheduleChangeKey}
          />
          <ProgressCard cronogramaId={resumo.id} startInfo={startInfo || { status: 'onTime' }} />
        </div>
        <div className='col-span-1 flex flex-col gap-3'>
          <StudyTimeProgress
            cronogramaId={resumo.id}
            refreshTrigger={scheduleChangeKey}
            startInfo={startInfo || { status: 'onTime' }}
          />
          <DisciplineReport
            cronogramaId={resumo.id}
            startInfo={startInfo || { status: 'onTime' }}
          />
        </div>
      </div>
      <WeekActivitySchedule
        cronogramaId={resumo.id}
        weeks={resumo.weeksArray}
        onScheduleChange={() => setScheduleChangeKey((k) => k + 1)}
        startInfo={startInfo || { status: 'onTime' }}
      />
      <AdjustScheduleModal
        isOpen={showAdjustScheduleModal}
        onClose={() => setShowAdjustScheduleModal(false)}
        selectedAction={adjustScheduleAction}
        onSelectAction={setAdjustScheduleAction}
        onDeleteConfirm={() => {
          setShowAdjustScheduleModal(false);
          setShowDeleteModal(true);
        }}
        onEditConfirm={(option) => {
          if (option === 'edit-hours' || option === 'reorganize') {
            const step = steps.find((item) => item.key === 'DailyWorkload');
            if (step) {
              setCurrentStep(step);
            }
            setShowAdjustScheduleModal(false);
            navigate(configStartPath);
            return;
          }

          if (option === 'extend-deadline') {
            const step = steps.find((item) => item.key === 'FinalDate');
            if (step) {
              setCurrentStep(step);
            }
            setShowAdjustScheduleModal(false);
            navigate(configStartPath);
            return;
          }
        }}
      />
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        scheduleId={resumo.id}
      />
    </div>
  );
};

export default ScheduleHub;
